import axios from 'axios';
import config from '../config';
import applyTokenRefreshInterceptor from '../utils/tokenRefreshInterceptor';
import applyAuthRetryInterceptor from '../utils/applyAuthRetryInterceptor';

const getToken = () => {
  return localStorage.getItem('token');
};

const clientHelperApiClient = axios.create({
  baseURL: config.api.clientServiceUrl,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 600000 // 10 minutes - increased for large document processing
});

clientHelperApiClient.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// On 401, attempt a token refresh and retry once before logging out
applyAuthRetryInterceptor(clientHelperApiClient);

// Proactively refresh token before any request if within 30-minute expiry window
applyTokenRefreshInterceptor(clientHelperApiClient);

const clientHelperService = {
  fetchClients: async () => {
    try {
      const response = await clientHelperApiClient.get('client-helper/clients');
      const data = response.data;
      // Convert object to array for compatibility with .find and .map
      const clientArray = Array.isArray(data) ? data : Object.values(data);
      return clientArray;
    } catch (error) {
      console.error('Error fetching clients:', error);
      return [];
    }
  },

  createKbClient: async ({ client_name, description }) => {
    const response = await clientHelperApiClient.post('client-helper/admin/clients', {
      client_name,
      description: description || '',
      nicknames: [],
    });
    return response.data; // { client_id, client_name, ... }
  },

  getDocumentTemplates: async () => {
    try {
      // Fetch document templates from the bedrock service endpoint
      const response = await axios.get(`${config.api.bedrockServiceUrl}/bedrock/document-templates`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      return response.data; // Expected format: { success: true, templates: [...] }
    } catch (error) {
      console.error('Error fetching document templates:', error);
      return { success: false, templates: [] };
    }
  },

  generateDocument: async ({ client_id, template_id, document_name, filter_mode, from_date, to_date, file_ids, kb_file_paths, output_bucket, output_keys }) => {
    try {
      const payload = {
        client_id,
        template_id,
        filter_mode: filter_mode || 'none'
      };
      
      // Add document name if provided
      if (document_name) payload.document_name = document_name;
      
      // Add filters based on mode
      if (filter_mode === 'manual') {
        if (from_date) payload.from_date = from_date;
        if (to_date) payload.to_date = to_date;
      }
      
      // Always include file_ids if provided (needed for templates without sections)
      if (file_ids && file_ids.length > 0) {
        payload.file_ids = file_ids;
        payload.kb_file_paths = kb_file_paths;
      }
      
      // Add output format parameters if specified
      if (output_bucket) {
        payload.output_bucket = output_bucket;
        payload.output_keys = output_keys;
      }
      
      const response = await axios.post(
        `${config.api.bedrockServiceUrl}/bedrock/generate-document`,
        payload,
        {
          headers: {
            'Authorization': `Bearer ${getToken()}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data; // Expected format: { success: true, execution_arn, execution_name, message }
    } catch (error) {
      console.error('Error generating document:', error);
      throw error;
    }
  },

  getSharePointFiles: async (client_id) => {
    try {
      // Fetch SharePoint file names from the vector service endpoint
      const response = await axios.get(`${config.api.vectorServiceUrl}/vectors/files/names`, {
        params: { client_id },
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      return response.data; // Expected format: { success: true, files: [...], client_id, count }
    } catch (error) {
      console.error('Error fetching SharePoint files:', error);
      return { success: false, files: [] };
    }
  },

  // include_emoney_data: also answer from the client's eMoney data (it wins on
  // figures); history: recent { question, answer } turns, so a follow-up can
  // be understood by the eMoney data.
  queryClient: async ({ client_id, question, session_id, from_date, to_date, generate_filters, doc_type_ids, sub_type_ids, file_ids, include_emoney_data, history }) => {
    try {
      const payload = {
        client_id,
        question,
        session_id: session_id || undefined,
        generate_filters: generate_filters !== undefined ? generate_filters : true,
        include_emoney_data: include_emoney_data !== undefined ? include_emoney_data : true,
      };
      if (history && history.length > 0) payload.history = history;
      if (from_date) payload.from_date = from_date;
      if (to_date) payload.to_date = to_date;
      if (doc_type_ids && doc_type_ids.length > 0) payload.doc_type_ids = doc_type_ids;
      if (sub_type_ids && sub_type_ids.length > 0) payload.sub_type_ids = sub_type_ids;
      if (file_ids && file_ids.length > 0) payload.file_ids = file_ids;
      
      // Initial query - returns job_id immediately
      const response = await clientHelperApiClient.post('client-helper/query', payload);
      const initialData = response.data;
      
      // Check if this is an async response (empty answer with job_id in prompt_history_id)
      if (!initialData.answer && initialData.prompt_history_id) {
        const jobId = initialData.prompt_history_id;
        console.log(`Query job started: ${jobId}, polling for completion...`);
        
        // Poll for completion (max 9 minutes)
        const maxPollTime = 540000; // 9 minutes in ms
        const pollInterval = 2000; // 2 seconds
        const startTime = Date.now();
        
        while ((Date.now() - startTime) < maxPollTime) {
          await new Promise(resolve => setTimeout(resolve, pollInterval));
          
          // Check job status
          const statusResponse = await clientHelperApiClient.get(`client-helper/query-status/${jobId}`);
          const statusData = statusResponse.data;
          
          // If answer is populated, job is complete
          if (statusData.answer) {
            console.log(`Query job ${jobId} completed successfully`);
            return statusData;
          }
          
          // Check if job failed (answer is an error message but prompt_history_id is null)
          if (!statusData.prompt_history_id && statusData.answer === "Sorry, I am unable to assist you with this request.") {
            console.error(`Query job ${jobId} failed`);
            return statusData;
          }
          
          // Continue polling...
          console.debug(`Query job ${jobId} still processing...`);
        }
        
        // Timeout after max poll time
        throw new Error('Query timed out after 9 minutes');
      }
      
      // If answer is already present, return immediately (sync response or cached)
      return initialData;
    } catch (error) {
      console.error('Error querying client knowledge base:', error);
      throw error;
    }
  },

  submitPromptFeedback: async ({ prompt_history_id, timestamp, score, comment }) => {
    try {
      // Use relative URL for feedback endpoint (handled by proxy or same origin)
      const response = await clientHelperApiClient.post('client-helper/prompt-feedback', {
        prompt_history_id,
        timestamp,
        score,
        comment
      });
      return response.data;
    } catch (error) {
      console.error('Error submitting prompt feedback:', error);
      throw error;
    }
  },

  checkFileExistence: async ({ client_id, files }) => {
    try {
      const response = await clientHelperApiClient.post('client-helper/check-file-existence', {
        client_id,
        files: files.map(file => file.name)
      });
      return response.data;
    } catch (error) {
      console.error('Error checking file existence:', error);
      throw error;
    }
  },

  uploadFile: async (file, client_id, onProgress) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('client_id', client_id);
      
      const response = await clientHelperApiClient.post('client-helper/upload-file', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  },

  uploadFiles: async (files, client_id, onProgress) => {
    const results = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      try {
        // Call onProgress with overall progress info
        if (onProgress) {
          onProgress({
            fileIndex: i,
            totalFiles: files.length,
            currentFileName: file.name,
            overallProgress: Math.round((i / files.length) * 100)
          });
        }
        
        const result = await clientHelperService.uploadFile(file, client_id, (fileProgress) => {
          // Call onProgress with file-specific progress
          if (onProgress) {
            onProgress({
              fileIndex: i,
              totalFiles: files.length,
              currentFileName: file.name,
              fileProgress: fileProgress,
              overallProgress: Math.round(((i + (fileProgress / 100)) / files.length) * 100)
            });
          }
        });
        
        results.push({ 
          success: true, 
          filename: file.name, 
          result 
        });
        
      } catch (error) {
        console.error(`Upload failed for ${file.name}:`, error);
        results.push({ 
          success: false, 
          filename: file.name, 
          error: error.message 
        });
      }
    }
    
    return results;
  },

  downloadFile: async (filename, client_id, doc_type, convertToPdf = false) => {
    try {
      console.log('Downloading file:', { filename, client_id, doc_type, convertToPdf });
      
      const params = { 
        filename: filename, 
        client_id: client_id, 
        doc_type: doc_type 
      };
      
      // Add convert_to_pdf parameter if needed
      if (convertToPdf) {
        params.convert_to_pdf = true;
      }
      
      // Get presigned URL from backend
      const response = await clientHelperApiClient.get(`client-helper/get-file`, {
        params: params
      });
      
      console.log('Response received:', response.status, response.data);
      
      const downloadUrl = response.data.download_url;
      if (!downloadUrl) {
        throw new Error('No download URL returned from server');
      }
      
      // Fetch the file directly from the presigned URL
      const fileResponse = await fetch(downloadUrl);
      
      if (!fileResponse.ok) {
        throw new Error(`Failed to download file: ${fileResponse.status}`);
      }
      
      const blob = await fileResponse.blob();
      console.log('Downloaded blob:', blob.size, blob.type);
      
      // Check if blob has content
      if (blob.size === 0) {
        throw new Error(`File not found or empty: ${filename}`);
      }
      
      // Ensure proper content type for PDF files
      if (filename.toLowerCase().endsWith('.pdf') || convertToPdf) {
        const pdfBlob = new Blob([blob], { type: 'application/pdf' });
        console.log('Created PDF blob:', pdfBlob.size, pdfBlob.type);
        return { blob: pdfBlob, headers: {} };
      }
      
      return { blob, headers: {} };
    }
    catch (error) {
      console.error('Error downloading file:', error);
      console.error('Error response:', error.response?.data, error.response?.status, error.response?.headers);
      throw error;
    }
  },

  // Fetch list of files in a client's knowledge base from the client service.
  fetchClientFiles: async (client_id, doc_type) => {
    try {
      const path = `client-helper/${client_id}/files/${doc_type}`;
      const response = await clientHelperApiClient.get(path);
      
      // The backend now returns a filtered array of files directly
      if (Array.isArray(response.data)) {
        return response.data;
      }
      
      // Fallback for any unexpected response format
      console.warn('Unexpected response format from fetchClientFiles:', response.data);
      return [];
    } catch (error) {
      console.error('fetchClientFiles: failed for client', client_id, 'doc_type', doc_type, error);
      return [];
    }
  },

  // Download a generated document by S3 path
  downloadGeneratedDocument: async (s3Path) => {
    try {
      console.log('Downloading generated document:', s3Path);
      
      // Get presigned URL from backend
      const response = await clientHelperApiClient.get('client-helper/get-generated-document', {
        params: { s3_path: s3Path }
      });
      
      console.log('Presigned URL response:', response.status, response.data);
      
      const downloadUrl = response.data.download_url;
      if (!downloadUrl) {
        throw new Error('No download URL returned from server');
      }
      
      // Fetch the file directly from the presigned URL
      const fileResponse = await fetch(downloadUrl);
      
      if (!fileResponse.ok) {
        throw new Error(`Failed to download file: ${fileResponse.status}`);
      }
      
      const blob = await fileResponse.blob();
      console.log('Downloaded blob:', blob.size, blob.type);
      
      // Check if blob has content
      if (blob.size === 0) {
        throw new Error(`File not found or empty`);
      }
      
      return { blob, headers: {} };
    } catch (error) {
      console.error('Error downloading generated document:', error);
      console.error('Error response:', error.response?.data, error.response?.status, error.response?.headers);
      throw error;
    }
  },

  // Fetch vectors directly from the vector service (uses absolute vectorServiceUrl)
  fetchVectors: async (filepath, limit = null, offset = null) => {
    try {
      const params = {};
      if (filepath) params.filepath = filepath;
      if (limit !== null) params.limit = limit;
      if (offset !== null) params.offset = offset;
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      // Ensure no trailing slash duplication
      const base = (config.api.vectorServiceUrl || '').replace(/\/$/, '');
      const url = `${base}/vectors`;
      const response = await axios.get(url, { params, headers, timeout: 60000 });
      return response.data; // expected { success, vectors, total_count, offset, limit }
    } catch (error) {
      console.error('Error fetching vectors from vector service:', error);
      throw error;
    }
  },

  // Get document generation jobs for a client
  getDocumentGenerationJobs: async (client_id) => {
    try {
      const response = await axios.get(
        `${config.api.bedrockServiceUrl}/bedrock/document-generation`,
        {
          params: { client_id },
          headers: {
            'Authorization': `Bearer ${getToken()}`
          }
        }
      );
      return response.data; // Expected format: { success: true, jobs: [...], total_count: n }
    } catch (error) {
      console.error('Error fetching document generation jobs:', error);
      throw error;
    }
  },

  // Get status of a specific document generation job
  getDocumentGenerationJobStatus: async (execution_id) => {
    try {
      const response = await axios.get(
        `${config.api.bedrockServiceUrl}/bedrock/document-generation/${execution_id}`,
        {
          headers: {
            'Authorization': `Bearer ${getToken()}`
          }
        }
      );
      return response.data; // Expected format: DocumentGenerationJobStatus
    } catch (error) {
      console.error('Error fetching document generation job status:', error);
      throw error;
    }
  },

  // Delete a document generation job and its associated files
  deleteDocumentGenerationJob: async (execution_id) => {
    try {
      const response = await axios.delete(
        `${config.api.bedrockServiceUrl}/bedrock/document-generation/${execution_id}`,
        {
          headers: {
            'Authorization': `Bearer ${getToken()}`
          }
        }
      );
      return response.data; // Expected format: { success: true, message: string, deleted_files: [...] }
    } catch (error) {
      console.error('Error deleting document generation job:', error);
      throw error;
    }
  },

  // Update a document generation job (e.g., document name)
  updateDocumentGenerationJob: async (execution_id, updates) => {
    try {
      const response = await axios.put(
        `${config.api.bedrockServiceUrl}/bedrock/document-generation/${execution_id}`,
        updates,
        {
          headers: {
            'Authorization': `Bearer ${getToken()}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data; // Expected format: { success: true, message: string, job: {...} }
    } catch (error) {
      console.error('Error updating document generation job:', error);
      throw error;
    }
  },

  // Get all prompts for template configuration
  getPrompts: async () => {
    try {
      const response = await axios.get(`${config.api.bedrockServiceUrl}/bedrock/prompts?lightweight=true`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      return response.data; // Expected format: { success: true, prompts: [...] }
    } catch (error) {
      console.error('Error fetching prompts:', error);
      return { success: false, prompts: [] };
    }
  },

  // Create a new document template
  createDocumentTemplate: async (templateData) => {
    try {
      const response = await axios.post(
        `${config.api.bedrockServiceUrl}/bedrock/document-templates`,
        templateData,
        {
          headers: {
            'Authorization': `Bearer ${getToken()}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating document template:', error);
      throw error;
    }
  },

  // Update an existing document template
  updateDocumentTemplate: async (templateId, templateData) => {
    try {
      const response = await axios.put(
        `${config.api.bedrockServiceUrl}/bedrock/document-templates/${templateId}`,
        templateData,
        {
          headers: {
            'Authorization': `Bearer ${getToken()}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating document template:', error);
      throw error;
    }
  },

  // Delete a document template
  deleteDocumentTemplate: async (templateId) => {
    try {
      const response = await axios.delete(
        `${config.api.bedrockServiceUrl}/bedrock/document-templates/${templateId}`,
        {
          headers: {
            'Authorization': `Bearer ${getToken()}`
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting document template:', error);
      throw error;
    }
  },

  // Get distinct knowledge base metadata values for filters
  getKBMetadataValues: async () => {
    try {
      const response = await axios.get(
        `${config.api.bedrockServiceUrl}/bedrock/kb-metadata-values`,
        {
          headers: {
            'Authorization': `Bearer ${getToken()}`
          }
        }
      );
      return response.data; // Expected format: { success: true, doc_types: [...], sub_types: [...] }
    } catch (error) {
      console.error('Error fetching KB metadata values:', error);
      return { success: false, doc_types: [], sub_types: [] };
    }
  },

  // Get LLM-suggested filters based on prompt text
  suggestKBFilters: async ({ promptText, availableDocTypes, availableSubTypes }) => {
    try {
      const response = await axios.post(
        `${config.api.bedrockServiceUrl}/bedrock/suggest-kb-filters`,
        {
          prompt_text: promptText,
          available_doc_types: availableDocTypes || [],
          available_sub_types: availableSubTypes || []
        },
        {
          headers: {
            'Authorization': `Bearer ${getToken()}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data; // Expected format: { success: true, suggested_filters: {...}, reasoning: "..." }
    } catch (error) {
      console.error('Error suggesting KB filters:', error);
      return { success: false, suggested_filters: null, reasoning: null };
    }
  }
};

export default clientHelperService;
